import React from "react";
import styles from "./style.module.scss";
import { MetricLap, MetricSpec } from "../../../types/performanceMetrics";
import MetricRow from "../MetricRow";

interface MetricGraphProps {
  metricName: string;
  metricSpec: MetricSpec;
  metricLaps: MetricLap[];
  scrollTargetRef?: React.RefObject<HTMLElement>;
}

function MetricGraph({
  metricName,
  metricSpec,
  metricLaps,
  scrollTargetRef,
}: MetricGraphProps) {
  const [maximisedGraphIndex, setMaximisedGraphIndex] = React.useState<
    number | null
  >(null);

  // Add safety checks for metricSpec properties
  const axesSpec = Array.isArray(metricSpec?.AxesSpec)
    ? metricSpec.AxesSpec
    : [metricSpec.AxesSpec];
  const plotsSpec = Array.isArray(metricSpec?.PlotsSpec)
    ? metricSpec.PlotsSpec
    : [metricSpec.PlotsSpec];

  const mutableMetricSpec: MetricSpec = {
    AxesSpec: [...axesSpec],
    PlotsSpec: [...plotsSpec],
  };

  // As Matt says...
  // "You get lap time for free at the bottom of every graph!"
  const lapTimeAxisSpec = {
    Axis: axesSpec.length + 1,
    Row: axesSpec.length + 1,
    Column: 1,
    YLabel: "Lap Time",
    Units: "s",
    ChannelYMin: null,
    ChannelYMax: null,
    DisplayWeighting: 1,
    RefLine: null,
    Warning: null,
    Critical: null,
    Location: 2,
  };

  const lapTimeMetricSpec = {
    ChannelName: "laptime",
    MetricType: "",
    Gating: "",
    GatingDef: "",
    Axis: lapTimeAxisSpec.Axis,
    LineStyle: "--",
    LineWidth: 1,
    Colour: "#000000ff",
    MarkerStyle: "^",
    MarkerFillColour: "#000000ff",
    MarkerEdgeColour: "#000000ff",
    MetricName: "Lap Time",
    DrawState: false,
  };

  mutableMetricSpec.AxesSpec.push(lapTimeAxisSpec);
  mutableMetricSpec.PlotsSpec.push(lapTimeMetricSpec);

  return (
    <div
      className={styles.container}
      id={`${metricName.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div
        className={maximisedGraphIndex !== null ? styles.hidden : styles.title}
      >
        {metricName}
      </div>
      <div className={styles.rowsContainer}>
        {mutableMetricSpec.AxesSpec.map((axisSpec, index) => {
          const isMaximised = maximisedGraphIndex === index;
          const isHidden = maximisedGraphIndex !== null && !isMaximised;
          return (
            <div
              className={
                isMaximised
                  ? styles.fullScreenGraph
                  : isHidden
                  ? styles.hidden
                  : styles.graphRow
              }
              style={
                !isMaximised && !isHidden
                  ? { height: `${100 / mutableMetricSpec.AxesSpec.length}%` }
                  : undefined
              }
              key={index}
            >
              <MetricRow
                axisSpec={axisSpec}
                plotSpecs={mutableMetricSpec.PlotsSpec}
                metricLaps={metricLaps}
                isMaximised={isMaximised}
                onMaximise={() => setMaximisedGraphIndex(index)}
                onMinimise={() => setMaximisedGraphIndex(null)}
                scrollTargetRef={scrollTargetRef}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MetricGraph;
